import { mapGetters, mapActions } from "vuex";
import { eventBus } from "@/main";
import { useFileOperations } from "@/composables/useFileOperations";
import { MAX_POLL_ATTEMPS, POLLING_INTERVAL } from "@/constants";
import { debounce } from "lodash";
import { isDicomFile } from "@/utils/daikonUtils";

export default {
  data() {
    return {
      lightBoxIndex: null,
      lightBoxList: [],
      isLoading: false,
      isFetching: false,
      limit: 100,
      highestId: 0,
      pollInterval: null,
      continuePolling: true,
      fetchAttempts: 0,
      uploadedFiles: null,
      spinnerToastShow: true,
      selectedFiles: [],
      storageListener: null,
    };
  },

  computed: {
    ...mapGetters({
      getFileList: "moduleMediaFile/getFileList",
      getFileTemplate: "moduleMediaFile/getFileTemplate",
      isFileStorageExpired: "moduleBranch/isFileStorageExpired",
    }),
  },

  methods: {
    ...mapActions({
      fetchMediaFiles: "moduleMediaFile/fecthMediaFiles",
      deleteMediaFile: "moduleMediaFile/deleteMediaFile",
      fetchRadiiViewUrl: "moduleMediaFile/fetchRadiiViewUrl",
      fecthMediaFilesTemplate: "moduleMediaFile/fecthMediaFilesTemplate",
    }),

    handlePreview(item) {
      // Check if it's a DICOM file
      if (isDicomFile(item)) {
        // Open DICOM viewer in new tab
        this.handleDicomView(item);
        return;
      }
      
      const list = this.filteredList(item.category);
      this.lightBoxIndex = list.findIndex(i => i.id === item.id);
      this.lightBoxList = list;
    },

    handleDicomView(item) {
      // Open DICOM viewer in new tab
      const originalUrl = item.src || item.url;
      
      // Use base64 encoding to completely avoid URL encoding issues
      // This prevents AWS security token corruption from URL parsing
      const base64Url = btoa(originalUrl);
      
      const dicomViewerUrl = `/dicom-viewer?data=${base64Url}`;
      window.open(dicomViewerUrl, '_blank', 'noopener,noreferrer');
    },

    resetLightbox() {
      this.lightBoxIndex = null;
      this.lightBoxList = [];
    },

    async openRadiiView({ src }) {
      try {
        const { data } = await this.fetchRadiiViewUrl({ imageUrl: src });
        window.open(data.viewer, "_blank");
      } catch (err) {
        console.error(err);
      }
    },

    async handleDelete(item) {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "ลบไฟล์นี้ ?",
          "คุณต้องการลบไฟล์นี้"
        );
        if (isConfirmed) {
          this.$set(item, "isDeleting", true);
          const res = await this.deleteMediaFile({
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: item.id,
          });
          if (res.status === 200) {
            this.selectedFiles = this.selectedFiles.filter(
              f => f.id !== item.id
            );
          }
        }
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showAlert("Error", "Failed to delete file");
      } finally {
        this.$set(item, "isDeleting", false);
      }
    },

    async handleBulkDelete() {
      try {
        const { isConfirmed } = await this.$refs.Dialog.showAlertConfirm(
          "Delete Selected Files?",
          `Are you sure you want to delete ${this.selectedFiles.length} files?`
        );

        if (isConfirmed) {
          const deletePromises = this.selectedFiles.map(async file => {
            this.$set(file, "isDeleting", true);
            try {
              const res = await this.deleteMediaFile({
                clinicUrl: this.$route.params.clinicUrl,
                branchUrl: this.$route.params.branchUrl,
                id: file.id,
              });
              return res.status === 200 ? file.id : null;
            } catch (err) {
              console.error(`Error deleting file ${file.id}:`, err);
              return null;
            } finally {
              this.$set(file, "isDeleting", false);
            }
          });

          const deletedIds = await Promise.all(deletePromises);
          const successfulDeletes = deletedIds.filter(id => id !== null);

          this.selectedFiles = this.selectedFiles.filter(
            file => !successfulDeletes.includes(file.id)
          );

          if (successfulDeletes.length > 0) {
            this.$bvToast.toast(
              `Successfully deleted ${successfulDeletes.length} files`,
              {
                title: "Success",
                variant: "success",
                solid: true,
              }
            );
          }
        }
      } catch (err) {
        console.error("Error in bulk delete:", err);
        this.$refs.Dialog.showAlert("Error", "Failed to delete files");
      }
    },

    async handleDownload(item) {
      try {
        const { downloadFile } = useFileOperations();
        await downloadFile(item, this.patient.id);
      } catch (error) {
        this.$refs.Dialog.showAlert("Download Error", error.message);
      }
    },

    async handleBulkDownload() {
      try {
        const { bulkDownload } = useFileOperations();
        await bulkDownload(this.selectedFiles, this.patient.id);
      } catch (error) {
        this.$refs.Dialog.showAlert(
          "Download Error",
          "Failed to download files"
        );
      }
    },

    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.id === file.id);
      if (index === -1) {
        this.selectedFiles.push(file);
      } else {
        this.selectedFiles.splice(index, 1);
      }
    },

    filteredList(category) {
      if (category === "Template") return this.getFileTemplate;
      return this.getFileList.filter(i => i.category === category);
    },

    async fetchFiles() {
      try {
        const response = await this.fetchMediaFiles({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          params: { patientId: this.patient.id, limit: this.limit },
        });

        const newFiles = response.filter(file => file.id > this.highestId);
        if (
          newFiles.length > 0 &&
          (this.uploadedFiles === null ||
            newFiles.length === this.uploadedFiles?.length)
        ) {
          this.continuePolling = false;
        } else {
          this.fetchAttempts++;
          if (this.fetchAttempts >= MAX_POLL_ATTEMPS) {
            this.continuePolling = false;
          }
        }
      } catch (error) {
        console.error("Error fetching files:", error);
      } finally {
        if (!this.continuePolling) {
          eventBus.$emit("hideSpinnerToast");
          clearInterval(this.pollInterval);
          if (this.getFileList.length > 0) {
            this.highestId = Math.max(...this.getFileList.map(file => file.id));
          }
          this.resetPollingData();
        }
      }
    },

    pollForUpdates(e) {
      if (this.mode === "document") return; // check to run in photo mode only
      if (this.spinnerToastShow) eventBus.$emit("showSpinnerToast");
      this.uploadedFiles = e;
      this.highestId = Math.max(...this.getFileList.map(file => file.id));
      this.pollInterval = setInterval(() => {
        this.fetchFiles();
      }, POLLING_INTERVAL);
    },

    pollForUpdatesTemplate(e) {
      if (this.spinnerToastShow) eventBus.$emit("showSpinnerToast");
      this.uploadedFiles = e;
      this.highestId = Math.max(...this.getFileTemplate.map(file => file.id));
      this.pollInterval = setInterval(() => {
        this.fetchFilesTemplate();
      }, POLLING_INTERVAL);
    },

    async fetchFilesTemplate() {
      try {
        const response = await this.fecthMediaFilesTemplate({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
        });

        const newFiles = response.filter(file => file.id > this.highestId);
        if (
          newFiles.length > 0 &&
          (this.uploadedFiles === null ||
            newFiles.length === this.uploadedFiles?.length)
        ) {
          this.continuePolling = false;
        } else {
          this.fetchAttempts++;
          if (this.fetchAttempts >= MAX_POLL_ATTEMPS) {
            this.continuePolling = false;
          }
        }
      } catch (error) {
        console.error("Error fetching template files:", error);
      } finally {
        if (!this.continuePolling) {
          eventBus.$emit("hideSpinnerToast");
          clearInterval(this.pollInterval);
          if (this.getFileTemplate.length > 0) {
            this.highestId = Math.max(
              ...this.getFileTemplate.map(file => file.id)
            );
          }
          this.resetPollingData();
        }
      }
    },

    resetPollingData() {
      this.highestId = 0;
      this.pollInterval = null;
      this.continuePolling = true;
      this.fetchAttempts = 0;
      this.spinnerToastShow = true;
    },

    openSignTab(item) {
      const { clinicUrl, branchUrl } = this.$route.params;
      const url = `${process.env.VUE_APP_FRONT_BASE_URL}/document`;

      function objectToParams(obj) {
        return Object.keys(obj)
          .filter(key => obj[key] !== null)
          .map(
            key => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key])
          )
          .join("&");
      }
      const params = {
        ...item,
        clinicUrl,
        branchUrl,
        patientId: this.patient?.id,
      };

      window.open(`${url}?${objectToParams(params)}`, "_blank");
    },

    showUploadModal(category) {
      this.$refs.uploadFiles.show({ category });
    },

    handleDirectUpload({ category, files }) {
      // For now, open the upload modal with the category
      // In the future, this could directly upload the files
      this.showUploadModal(category);
      
    },

    handleUploadSuccess({ category, count, files = [] }) {
      // Trigger polling to refresh file list
      this.pollForUpdates(files);
    },

    handleUploadError(error) {
      console.error('Upload error:', error);
      this.$bvToast.toast(
        "เกิดข้อผิดพลาดในการอัปโหลดไฟล์",
        {
          title: "อัปโหลดล้มเหลว",
          variant: "danger",
          solid: true,
          autoHideDelay: 5000,
        }
      );
    },

    handleStorageEvent(e) {
      if (e.key === "crossTabEvent" && e.newValue) {
        try {
          const eventData = JSON.parse(e.newValue);

          if (
            eventData.type === "POLL_FOR_UPDATE" &&
            eventData.payload?.source === "signDoc"
          ) {
            this.pollForUpdates(eventData.payload.files);
            localStorage.removeItem("crossTabEvent");
          }
        } catch (error) {
          console.error("Error processing storage event:", error);
        }
      }
    },
  },

  mounted() {
    eventBus.$on("pollForUpdates", this.pollForUpdates);

    this.storageListener = this.handleStorageEvent.bind(this);
    window.addEventListener("storage", this.storageListener);
  },

  beforeDestroy() {
    eventBus.$off("pollForUpdates", this.pollForUpdates);
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
    }
  },
};